// Read individual env values off the LIVE gateway container app.
//
// Why this exists: deploy/main.bicep declares `voiceInternalSecret` with a
// `newGuid()` default, which re-evaluates on every deployment. Any Bicep re-run
// that does not pass the current value therefore mints a new shared secret and
// rolls BOTH the gateway and the voice relay. Re-run paths read the live value
// back and pass it through — read the deployed state, never reconstruct it.
//
// SECRET HYGIENE: the returned value is used in memory only. It is never logged,
// never written to a file, and never stored in the system/infra-params row.
import { runAz } from "./az.js";

/**
 * Read one env var from the gateway container app's first container.
 * Returns "" when the app has no such var, or when the read fails — callers
 * treat "" as "omit the param", which preserves first-deploy behaviour.
 */
export async function readLiveGatewayEnvVar(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
  name: string;
}): Promise<string> {
  const args = ["containerapp", "show", "-n", opts.gatewayName, "-g", opts.resourceGroup];
  if (opts.subscriptionId) args.push("--subscription", opts.subscriptionId);
  args.push("--query", `properties.template.containers[0].env[?name=='${opts.name}'].value | [0]`, "-o", "tsv");
  let raw: string;
  try {
    raw = await runAz(args);
  } catch {
    return "";
  }
  const v = raw.trim();
  // `az … -o tsv` prints the literal `None` for a null scalar on some versions.
  return v === "None" ? "" : v;
}

/**
 * The image the gateway container app is ACTUALLY running. Returns "" when the
 * read fails or there is no gateway yet (a from-zero deploy), which callers
 * treat as "no live image to preserve".
 *
 * An infra converge must not re-point the gateway at the manifest's image. The
 * gateway roll is `applyGatewayImage`'s job, and it deliberately REFUSES a
 * BYOC/private-ACR gateway (marking it externally managed) rather than dragging
 * it onto the tracked public repo. Passing the manifest's ref into bicep
 * bypassed that refusal — and on any install whose container app authenticates
 * to its registry with a managed identity, Azure rejects the deployment
 * outright: ContainerAppRegistryServerNotAllowedForManagedIdentity, "the
 * registry server 'ghcr.io' is not allowed when using managed identities".
 * Live-proven on m8t's own canary, 2026-08-11.
 */
export async function readLiveGatewayImage(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
}): Promise<{ image: string } | { error: string }> {
  const args = ["containerapp", "show", "-n", opts.gatewayName, "-g", opts.resourceGroup];
  if (opts.subscriptionId) args.push("--subscription", opts.subscriptionId);
  args.push("--query", "properties.template.containers[0].image", "-o", "tsv");
  try {
    const v = (await runAz(args)).trim();
    // `az … -o tsv` prints the literal `None` for a null scalar on some versions.
    return { image: v === "None" ? "" : v };
  } catch (e) {
    // Deliberately NOT swallowed to "" like readLiveGatewayEnvVar above. That
    // sibling's "" means "the app has no such env var", a real and harmless
    // state. Here "" would have to mean two different things — "no gateway
    // exists" and "the read failed" — and the caller's behaviour must differ:
    // guessing an image on a failed read re-points a live gateway.
    return { error: (e as Error).message };
  }
}

/** The voice relay ↔ gateway shared secret, read off live state. Never log the result. */
export function readLiveVoiceInternalSecret(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
}): Promise<string> {
  return readLiveGatewayEnvVar({ ...opts, name: "M8T_INTERNAL_SECRET" });
}
